// Community posts and comments (Core: /member/posts, /member/comments, /member/reports).
// Pure helpers – own-property reads only; nothing is persisted in the browser.
import { own, lookup, text } from "./memberDerive";
import { membershipState, isGroupPaused, PAUSED_WRITE_TEXT } from "./groupMembership";

export const POST_MAX = 2000;
export const COMMENT_MAX = 1000;

const POST_STATUS = { sichtbar: "visible", ausgeblendet: "hidden" };
const POST_AUDIENCE = { community: "community", group: "group" };
const str = (v) => (typeof v === "string" ? v : "");
const idOf = (v) => (typeof v === "string" || typeof v === "number" ? String(v) : "");

// Trimmed length in Unicode code points (not UTF-16 units).
export function textLimit(raw, max) {
  const value = String(raw ?? "").trim();
  const length = Array.from(value).length;
  return { value, length, error: !length ? "Bitte schreibe einen Text." : length > max ? `Bitte höchstens ${max} Zeichen (aktuell ${length}).` : "" };
}

function person(v) {
  return { id: idOf(own(v, "id")), name: str(own(v, "name")) || "Mitglied" };
}

export function normalizePost(p) {
  if (!p || typeof p !== "object" || !idOf(own(p, "id"))) return null;
  const aud = own(p, "audience");
  const type = lookup(POST_AUDIENCE, own(aud, "type")) || "unknown";
  const count = Number(own(p, "comments_count"));
  return {
    id: idOf(own(p, "id")),
    author: person(own(p, "author")),
    isMine: own(p, "is_mine") === true,
    canEdit: own(p, "can_edit") === true,
    audience: { type, groupId: idOf(own(aud, "group_id")), groupName: text(own(aud, "group_name")) },
    body: str(own(p, "body")),
    status: lookup(POST_STATUS, own(p, "status")) || "unknown",
    createdAt: str(own(p, "created_at")) || null,
    editedAt: str(own(p, "edited_at")) || null,
    commentsCount: Number.isInteger(count) && count >= 0 ? count : null,
    moderationNote: str(own(p, "moderation_note")),
    image: own(p, "image") ?? null,
  };
}

export function normalizeComment(c) {
  if (!c || typeof c !== "object" || !idOf(own(c, "id"))) return null;
  return {
    id: idOf(own(c, "id")),
    postId: idOf(own(c, "post_id")),
    author: person(own(c, "author")),
    isMine: own(c, "is_mine") === true,
    canEdit: own(c, "can_edit") === true,
    body: str(own(c, "body")),
    status: lookup(POST_STATUS, own(c, "status")) || "unknown",
    createdAt: str(own(c, "created_at")) || null,
    editedAt: str(own(c, "edited_at")) || null,
  };
}

export function normalizePostList(d) {
  const list = own(d, "posts");
  return Array.isArray(list) ? list.map(normalizePost).filter(Boolean) : null;
}

export function normalizePostDetail(d) {
  const post = normalizePost(own(d, "post"));
  if (!post) return null;
  const comments = own(d, "comments");
  return { post, comments: Array.isArray(comments) ? comments.map(normalizeComment).filter(Boolean) : [] };
}

// Only own content may change; editing additionally needs can_edit. Hidden posts get no comments.
export function contentPermissions(item, { post = false } = {}) {
  const visible = item.status === "visible";
  return {
    edit: item.isMine && item.canEdit && visible,
    remove: item.isMine && item.status !== "unknown",
    report: !item.isMine && !!item.author.id,
    comment: post ? visible : false,
  };
}

export function audienceLabel(a) {
  if (a.type === "group") return `Kreis: ${a.groupName || "unbekannter Kreis"}`;
  if (a.type === "community") return "Mitglieder-Community";
  return "Zielgruppe unbekannt";
}

export const confirmedGroups = (groups) => (Array.isArray(groups) ? groups : []).filter((g) => membershipState(g) === "member");

// List filter → query; only parameters the Core supports, nothing persisted.
export function postsPath(filter, groupId) {
  if (filter === "group" && groupId) return `/member/posts?audience=group&group_id=${encodeURIComponent(groupId)}`;
  if (filter === "community") return "/member/posts?audience=community";
  return "/member/posts?audience=all";
}

// Builds the POST body or explains why nothing may be sent. "only_me" is a private local draft and is never sent.
export function publishPlan({ body, audience, groups, hasImage }) {
  const t = textLimit(body, POST_MAX);
  if (audience.type === "only_me") return { ok: false, reason: "only_me", error: "„Nur ich“ ist ein privater Entwurf auf diesem Gerät und wird nicht gesendet." };
  if (t.error) return { ok: false, reason: "body", error: t.error };
  if (hasImage) return { ok: false, reason: "image", error: "Bilder können noch nicht hochgeladen werden. Entferne das Bild, um ohne Bild zu veröffentlichen." };
  if (audience.type === "community") {
    if (audience.confirmed !== true) return { ok: false, reason: "reach", error: "Bitte bestätige bewusst, dass alle angemeldeten Mitglieder den Beitrag sehen." };
    return { ok: true, payload: { body: t.value, audience_type: "community" } };
  }
  if (audience.type === "group") {
    const g = confirmedGroups(groups).find((x) => String(x.id) === String(audience.groupId));
    if (!g) return { ok: false, reason: "group", error: "Bitte wähle einen Kreis, in dem du bestätigtes Mitglied bist." };
    if (isGroupPaused(g)) return { ok: false, reason: "paused", error: PAUSED_WRITE_TEXT };
    return { ok: true, payload: { body: t.value, audience_type: "group", group_id: g.id } };
  }
  return { ok: false, reason: "audience", error: "Bitte wähle eine Zielgruppe." };
}

// A mutation counts as saved only with a positive, matching server answer.
export const savedPost = (d) => normalizePost(own(d, "post"));
export const savedComment = (d) => normalizeComment(own(d, "comment"));
export const removed = (d) => own(d, "ok") === true && own(d, "status") === "entfernt_autor";

export const UNCLEAR_TEXT = "Die Veröffentlichung/Änderung konnte nicht bestätigt werden. Dein Text bleibt hier. Bitte prüfe den aktuellen Stand, bevor du erneut sendest.";
export const UNCLEAR_REMOVE_TEXT = "Das Entfernen konnte nicht bestätigt werden. Bitte prüfe den aktuellen Stand, bevor du es noch einmal versuchst.";

// saved = confirmed by the server; rejected = definite refusal; everything else is unclear (it may have been saved).
export function writeOutcome(r, confirmed, unclearText = UNCLEAR_TEXT) {
  if (r && typeof r.rejected === "string" && r.rejected) return { state: "rejected", error: r.rejected };
  if (confirmed) return { state: "saved", error: "" };
  return { state: "unclear", error: unclearText };
}

export const postHref = (id) => `/community/beitraege/${encodeURIComponent(id)}`;
